import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUpRight, TrendingUp, Target, Wallet, Layers,
  Users2, ChevronDown, Sparkles, BarChart3, Building2, Shield, Globe2, MapPin,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { fmtCurrency, COUNTRY_FLAGS } from "../components/infoshop/utils";
import Flag from "../components/Flag";
import { displayCompany } from "../branding/tenants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: "me", label: "My Dashboard", icon: Wallet },
  { id: "org", label: "Organization", icon: Building2 },
];

function compact(n) {
  if (n === null || n === undefined) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function KPI({ label, value, sub, tint = "blue", icon: Icon }) {
  const map = {
    blue: "text-[#0072CE] bg-[#0072CE]/10 border-[#0072CE]/20",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-800 bg-amber-50 border-amber-200",
    slate: "text-slate-700 bg-slate-100 border-slate-200",
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
        {Icon && (
          <div className={`h-9 w-9 rounded-md flex items-center justify-center border ${map[tint]}`}>
            <Icon size={16} />
          </div>
        )}
      </div>
      <div className="font-display text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-slate-900">
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function YearBars({ data, currency }) {
  // data = [{ year, spend, savings }, ...]
  const max = Math.max(...data.map((d) => d.spend), 1);
  return (
    <div className="grid grid-cols-3 gap-6">
      {data.map((d) => {
        const spendPct = (d.spend / max) * 100;
        const savePct = d.spend ? (d.savings / d.spend) * 100 : 0;
        return (
          <div key={d.year} className="space-y-2" data-testid={`year-bar-${d.year}`}>
            <div className="flex items-baseline justify-between">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{d.year}</div>
              {d.isProjection && (
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  Run-rate
                </div>
              )}
            </div>
            <div className="font-display text-xl md:text-2xl font-bold text-slate-900">
              {fmtCurrency(d.spend, currency)}
            </div>
            <div className="text-xs text-emerald-700 font-medium">
              {fmtCurrency(d.savings, currency)} savings · {Math.round(savePct)}%
            </div>
            <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-slate-900" style={{ width: `${spendPct}%` }} />
            </div>
            <div className="h-1 w-full rounded-full bg-emerald-50 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(d.savings / max) * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthlyChart({ rows, currency }) {
  const max = Math.max(...rows.map((r) => r.spend), 1);
  return (
    <div className="grid grid-cols-12 gap-1.5 items-end h-44">
      {rows.map((m) => {
        const spendH = Math.max(2, (m.spend / max) * 100);
        const savingsH = m.spend ? Math.max(0, (m.savings / m.spend) * spendH) : 0;
        return (
          <div key={m.month} className="flex flex-col items-stretch gap-1.5 h-full" data-testid={`bar-${m.month}`}>
            <div className="flex-1 flex flex-col justify-end">
              <div className="relative w-full rounded-t-sm overflow-hidden bg-slate-100" style={{ height: `${spendH}%` }} title={`${m.label}: ${fmtCurrency(m.spend, currency)}`}>
                <div className="absolute inset-x-0 bottom-0 bg-slate-900" style={{ height: "100%" }} />
                <div className="absolute inset-x-0 bottom-0 bg-emerald-500/85" style={{ height: `${savingsH}%` }} />
              </div>
            </div>
            <div className="text-[10px] text-center text-slate-500 font-mono">{m.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function CategoryRow({ row, max, currency }) {
  const pct = max ? (row.spend / max) * 100 : 0;
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-800 font-medium">{row.category}</div>
        <div className="text-slate-900 font-display font-semibold">{fmtCurrency(row.spend, currency)}</div>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-[#0072CE]" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
        <span>{row.orders} order{row.orders === 1 ? "" : "s"}</span>
        <span className="text-emerald-700 font-medium">{fmtCurrency(row.savings, currency)} saved</span>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("me");
  const [data, setData] = useState(null);
  const [targetCompany, setTargetCompany] = useState("");
  const [orgError, setOrgError] = useState(null);
  const [loading, setLoading] = useState(true);

  // One-shot mount-time bootstrap — intentionally empty deps.
  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const overrides = {};
      ["network_id", "company_name", "country_name", "country_code"].forEach((k) => {
        const v = params.get(k); if (v) overrides[k] = v;
      });
      const { data: s } = await axios.get(`${API}/infoshop/session`, { params: overrides });
      setSession(s);
      try {
        const { data: p } = await axios.get(`${API}/infoshop/profile`, {
          params: { network_id: s.network_id, company_name: s.company_name },
        });
        setProfile(p.profile);
      } catch (err) {
        console.warn("[Dashboard] profile load failed (non-fatal):", err);
      }
    })();
     
  }, []);

  useEffect(() => {
    if (!session) return;
    setLoading(true);
    setOrgError(null);
    const url = `${API}/infoshop/dashboard/${tab}`;
    const params = { network_id: session.network_id, company_name: session.company_name };
    if (tab === "org" && targetCompany) params.target_company = targetCompany;
    axios.get(url, { params })
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (tab === "org") setOrgError(err?.response?.data?.detail || "Org dashboard unavailable");
        else toast.error(err?.response?.data?.detail || "Could not load dashboard");
      })
      .finally(() => setLoading(false));
  }, [session, tab, targetCompany]);

  const cur = data?.currency || "EUR";
  const flag = session ? COUNTRY_FLAGS[session.country_code] || "🌐" : "";

  const yoy = useMemo(() => {
    if (!data) return [];
    if (data.scope === "me") {
      return [
        { year: data.two_years_ago.year, spend: data.two_years_ago.spend, savings: data.two_years_ago.savings },
        { year: data.previous_year.year, spend: data.previous_year.spend, savings: data.previous_year.savings },
        { year: data.current_year, spend: data.ytd_spend, savings: data.ytd_savings, isProjection: true },
      ];
    }
    return [
      { year: data.two_years_ago.year, spend: data.two_years_ago.spend, savings: data.two_years_ago.savings },
      { year: data.previous_year.year, spend: data.previous_year.spend, savings: data.previous_year.savings },
      { year: data.current_year, spend: Math.max(data.ytd_spend, 0), savings: Math.max(data.ytd_savings, 0), isProjection: true },
    ];
  }, [data]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/infoshop")} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 text-sm" data-testid="dash-back">
            <ArrowLeft size={14} /> UniBuy™
          </button>
          <div className="h-4 w-px bg-slate-200" />
          <div className="leading-tight">
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">UniBuy™ Analytics</div>
            <div className="font-display font-bold text-slate-900 text-base tracking-tight">Spend & Savings Dashboard</div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200" data-testid="dash-scope-pill">
              <Flag code={session?.country_code} height={14} />
              <span className="text-xs font-mono text-slate-700">{session?.network_id}</span>
              {session?.company_name && (
                <>
                  <span className="h-3 w-px bg-slate-300" />
                  <span className="text-xs font-semibold text-slate-800">{displayCompany(session.company_name)}</span>
                </>
              )}
              {profile?.title && (
                <>
                  <span className="h-3 w-px bg-slate-300" />
                  <span className="text-xs text-slate-600">{profile.title}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 lg:px-8 -mb-px flex gap-1">
          {TABS.map((t) => {
            const TabIcon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${active ? "border-[#0072CE] text-[#0072CE]" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              >
                <TabIcon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 space-y-6">
        {/* Org permission gate */}
        {tab === "org" && orgError && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-6 flex items-start gap-4" data-testid="org-permission-error">
            <div className="h-10 w-10 rounded-md bg-rose-100 border border-rose-200 text-rose-700 flex items-center justify-center shrink-0">
              <Shield size={18} />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg font-semibold text-slate-900 tracking-tight">Restricted view</div>
              <p className="mt-1 text-sm text-slate-700">{orgError}</p>
              <Link to="/infoshop" className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#0072CE] hover:underline">
                Update your title from the UniBuy™ user menu →
              </Link>
            </div>
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 rounded-2xl bg-white border border-slate-200 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && data && data.scope === "me" && (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPI label={`YTD Spend (${data.current_year})`} value={fmtCurrency(data.ytd_spend, cur)} sub={`${data.ytd_orders} orders`} tint="blue" icon={Wallet} />
              <KPI label="YTD Savings" value={fmtCurrency(data.ytd_savings, cur)} sub={`${data.avg_effective_discount_pct}% effective discount`} tint="emerald" icon={TrendingUp} />
              <KPI label="Avg Order Value" value={fmtCurrency(data.ytd_orders ? data.ytd_spend / data.ytd_orders : 0, cur)} sub="per cart transferred" tint="slate" icon={Target} />
              <KPI label="Top Category" value={data.categories[0]?.category || "—"} sub={data.categories[0] ? fmtCurrency(data.categories[0].spend, cur) : ""} tint="amber" icon={Layers} />
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Spend & Savings · Year over Year</div>
                    <div className="font-display text-lg font-semibold text-slate-900 tracking-tight mt-1" data-testid="me-trend-heading">{displayCompany(data.company_name)} — three-year trend</div>
                  </div>
                  <div className="text-xs text-slate-500">Bars = Spend · Green = Savings</div>
                </div>
                <YearBars data={yoy} currency={cur} />
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{data.current_year} · Monthly</div>
                <div className="font-display text-lg font-semibold text-slate-900 tracking-tight mt-1 mb-4">Spend pacing</div>
                <MonthlyChart rows={data.monthly_trend} currency={cur} />
              </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Top categories</div>
                <div className="font-display text-lg font-semibold text-slate-900 tracking-tight">Where your spend goes</div>
                <div className="mt-4 divide-y divide-slate-100">
                  {data.categories.length === 0 && <div className="text-sm text-slate-500 py-6">No orders yet — your category breakdown will populate once you transfer a cart.</div>}
                  {data.categories.slice(0, 8).map((row) => (
                    <CategoryRow key={row.category} row={row} max={data.categories[0]?.spend || 1} currency={cur} />
                  ))}
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Recent orders</div>
                <div className="font-display text-lg font-semibold text-slate-900 tracking-tight">Last 6 transfers</div>
                <div className="mt-4 space-y-2">
                  {data.recent_orders.length === 0 && <div className="text-sm text-slate-500 py-6">Transfer your first cart to see history here.</div>}
                  {data.recent_orders.map((o) => (
                    <Link key={o.order_id} to="/infoshop" className="block rounded-lg border border-slate-200 p-3 hover:border-[#0072CE] transition-colors" data-testid={`me-order-${o.order_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-sm font-semibold text-slate-900">{o.order_id}</div>
                        <div className="font-display font-bold text-slate-900">{fmtCurrency(o.total_value, o.currency)}</div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{o.status}</span>
                        <span className="text-[11px] text-slate-500">{o.lines} line{o.lines === 1 ? "" : "s"}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {!loading && data && data.scope === "org" && (
          <>
            {/* Admin tenant selector */}
            {data.viewer?.is_admin && data.available_tenants && data.available_tenants.length > 0 && (
              <div
                className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
                data-testid="admin-tenant-selector"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-[#0072CE]/10 border border-[#0072CE]/20 text-[#0072CE] flex items-center justify-center">
                    <Building2 size={18} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                      Infosys Aggregator · Admin view
                    </div>
                    <div className="font-display text-base font-semibold text-slate-900 tracking-tight">
                      {data.is_cross_tenant
                        ? `All customer tenants combined · ${data.available_tenants.length} active`
                        : `Scoped to ${displayCompany(data.company_name)}`}
                    </div>
                  </div>
                </div>
                <div className="md:ml-auto flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setTargetCompany("")}
                    data-testid="tenant-pill-all"
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      !targetCompany
                        ? "bg-[#0072CE] text-white border-[#0072CE]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    All Tenants
                  </button>
                  {data.available_tenants.map((co) => (
                    <button
                      key={co}
                      type="button"
                      onClick={() => setTargetCompany(co)}
                      data-testid={`tenant-pill-${co}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        targetCompany === co
                          ? "bg-[#0072CE] text-white border-[#0072CE]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {co}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPI label={`YTD Spend (${data.current_year})`} value={fmtCurrency(data.ytd_spend, cur)} sub={`Target ${fmtCurrency(data.cy_target_spend, cur)}`} tint="blue" icon={Wallet} />
              <KPI label="YTD Savings" value={fmtCurrency(data.ytd_savings, cur)} sub={`Target ${fmtCurrency(data.cy_target_savings, cur)}`} tint="emerald" icon={TrendingUp} />
              <KPI label="Active Buyers" value={data.active_buyers} sub={`${data.ytd_orders} orders YTD`} tint="slate" icon={Users2} />
              <KPI label="Avg Effective Discount" value={`${data.avg_effective_discount_pct}%`} sub="across all categories" tint="amber" icon={Target} />
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Org · Year over Year</div>
                    <div className="font-display text-lg font-semibold text-slate-900 tracking-tight mt-1">{displayCompany(data.company_name)} — three-year trend</div>
                  </div>
                  <div className="text-xs text-slate-500">For CPO · CFO · CSO reporting</div>
                </div>
                <YearBars data={yoy} currency={cur} />
                <div className="mt-6">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{data.current_year} pacing vs prior years</div>
                  <MonthlyChart rows={data.monthly_trend_cy} currency={cur} />
                </div>
              </div>

              <div className="rounded-2xl bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Top buyers</div>
                  <Users2 size={14} className="text-slate-400" />
                </div>
                <div className="font-display text-lg font-semibold text-slate-900 tracking-tight mt-1 mb-4">Who&apos;s spending</div>
                {data.top_buyers.length === 0 && <div className="text-sm text-slate-500">No buyer activity yet.</div>}
                <div className="space-y-2">
                  {data.top_buyers.map((b) => (
                    <div key={`${b.company_name}-${b.network_id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 gap-3" data-testid={`top-buyer-${b.network_id}`}>
                      <div className="min-w-0 flex items-start gap-2">
                        {b.country_code && (
                          <Flag code={b.country_code} height={12} className="mt-0.5" />
                        )}
                        <div className="min-w-0">
                          <div className="font-mono text-sm font-semibold text-slate-900 truncate">{b.network_id}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {b.title || "—"}
                            {data.is_cross_tenant && b.company_name ? ` · ${b.company_name}` : ""}
                            {" "}· {b.orders} order{b.orders === 1 ? "" : "s"}
                          </div>
                        </div>
                      </div>
                      <div className="font-display font-bold text-slate-900 shrink-0">{fmtCurrency(b.spend, cur)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Category breakdown</div>
                  <div className="font-display text-lg font-semibold text-slate-900 tracking-tight mt-1">Org-wide spend by category</div>
                </div>
                <BarChart3 size={16} className="text-slate-400" />
              </div>
              {data.categories.length === 0 && <div className="text-sm text-slate-500 py-6">No categorized spend yet.</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                <div className="divide-y divide-slate-100 md:pr-4">
                  {data.categories.slice(0, 6).map((row) => (
                    <CategoryRow key={row.category} row={row} max={data.categories[0]?.spend || 1} currency={cur} />
                  ))}
                </div>
                <div className="divide-y divide-slate-100 md:pl-4">
                  {data.categories.slice(6, 12).map((row) => (
                    <CategoryRow key={row.category} row={row} max={data.categories[0]?.spend || 1} currency={cur} />
                  ))}
                </div>
              </div>
            </section>

            {/* Plant / Country opportunity */}
            {data.plant_clusters && data.plant_clusters.length > 0 && (
              <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="plant-cluster-section">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Opportunity by plant · country</div>
                    <div className="font-display text-lg font-semibold text-slate-900 tracking-tight mt-1">
                      Ranked by realized savings
                    </div>
                  </div>
                  <Globe2 size={16} className="text-slate-400" />
                </div>
                <p className="text-xs text-slate-500 max-w-2xl">
                  Groups buyers by country of operation. Use this view to drive friendly competition between plants,
                  identify under-penetrated geographies and prioritize the next round of incumbent-supplier consolidation.
                </p>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.plant_clusters.map((c, idx) => {
                    const maxSave = Math.max(...data.plant_clusters.map((x) => x.savings), 1);
                    const pct = (c.savings / maxSave) * 100;
                    return (
                      <div
                        key={c.country_code}
                        data-testid={`plant-cluster-${c.country_code}`}
                        className="rounded-xl border border-slate-200 p-4 flex flex-col gap-3 hover:border-[#0072CE] transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Flag code={c.country_code} height={20} />
                            <div className="min-w-0">
                              <div className="font-display text-sm font-semibold text-slate-900 truncate">
                                {c.country_name}
                              </div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                Rank #{idx + 1}
                              </div>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-display text-lg font-bold text-emerald-700">
                              {fmtCurrency(c.savings, cur)}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">saved</div>
                          </div>
                        </div>

                        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Spend</div>
                            <div className="text-sm font-semibold text-slate-900 font-display mt-0.5">
                              {fmtCurrency(c.spend, cur)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Orders</div>
                            <div className="text-sm font-semibold text-slate-900 font-display mt-0.5">{c.orders}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Buyers</div>
                            <div className="text-sm font-semibold text-slate-900 font-display mt-0.5">{c.buyers_count}</div>
                          </div>
                        </div>

                        {c.top_buyer && (
                          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 flex items-center gap-2">
                            <MapPin size={12} className="text-slate-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Plant champion</div>
                              <div className="font-mono text-[11px] text-slate-800 truncate">{c.top_buyer.network_id}</div>
                            </div>
                            <div className="font-display font-bold text-xs text-slate-900 shrink-0">
                              {fmtCurrency(c.top_buyer.spend, cur)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Cross-tenant rollup — only when Infosys admin viewing All Tenants */}
            {data.is_cross_tenant && data.tenants && data.tenants.length > 0 && (
              <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="tenants-rollup-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Customer tenants</div>
                    <div className="font-display text-lg font-semibold text-slate-900 tracking-tight mt-1">
                      Per-customer rollup — click to scope
                    </div>
                  </div>
                  <Building2 size={16} className="text-slate-400" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.tenants.map((t) => (
                    <button
                      key={t.company_name}
                      type="button"
                      onClick={() => setTargetCompany(t.company_name)}
                      data-testid={`tenant-card-${t.company_name}`}
                      className="text-left rounded-xl border border-slate-200 hover:border-[#0072CE] p-4 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-display text-base font-semibold text-slate-900 tracking-tight">
                          {t.company_name}
                        </div>
                        <ArrowUpRight size={14} className="text-slate-400" />
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                        {t.countries.map((cc) => (
                          <Flag key={cc} code={cc} height={10} title={cc} />
                        ))}
                        <span>·</span>
                        <span>{t.buyers} buyer{t.buyers === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>{t.orders} orders</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Spend</div>
                          <div className="font-display text-lg font-bold text-slate-900">{fmtCurrency(t.spend, cur)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Savings</div>
                          <div className="font-display text-lg font-bold text-emerald-700">{fmtCurrency(t.savings, cur)}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-2xl bg-slate-950 text-white border border-slate-900 p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#7DD3FC]"><Sparkles size={12} /> Opportunity sizing — next step</div>
                  <div className="font-display text-xl font-semibold tracking-tight mt-1">
                    Want to size additional savings on incumbent spend?
                  </div>
                  <p className="mt-1 text-sm text-slate-300 max-w-xl">
                    Upload past purchases via the OMNIMatch AI workbench and Infosys Procurement
                    will quantify the consolidation opportunity vs your incumbent distributors.
                  </p>
                </div>
                <Link
                  to="/infoshop/admin/enrichment"
                  data-testid="org-cta-enrichment"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 rounded-sm px-5 py-3 font-semibold text-sm hover:bg-slate-100"
                >
                  Open OMNIMatch AI <ArrowUpRight size={14} />
                </Link>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
